import { useAuth } from "@/components/features/auth/hooks/use-auth";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { user } from "../api/queries.client";
import { followUser, unfollowUser } from "../api/users.client";

interface UseUserFollowMutationProps {
  followeeId: string;
  username: string;
  onSuccess?: (isFollowing: boolean) => void;
  onError?: (error: Error) => void;
}

export function useUserFollowMutation({
  followeeId,
  username,
  onSuccess,
  onError,
}: UseUserFollowMutationProps) {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();

  // Get the standardized query keys
  const followingInfoQuery = user.getFollowingInfo({
    userId: followeeId,
    viewerId: currentUser?.uid,
  });

  return useMutation({
    mutationFn: async ({ shouldFollow }: { shouldFollow: boolean }) => {
      if (!currentUser?.uid) {
        throw new Error("User not authenticated");
      }

      // Prevent self-following
      if (currentUser.uid === followeeId) {
        throw new Error("You cannot follow yourself");
      }

      if (shouldFollow) {
        return await followUser({
          followerId: currentUser.uid,
          followeeId,
        });
      } else {
        return await unfollowUser({
          followerId: currentUser.uid,
          followeeId,
        });
      }
    },
    onMutate: async ({ shouldFollow }) => {
      // Cancel any outgoing refetches using the standardized query key
      await queryClient.cancelQueries({
        queryKey: followingInfoQuery.queryKey,
      });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(
        followingInfoQuery.queryKey,
      );

      // Optimistically update the cache
      queryClient.setQueryData(followingInfoQuery.queryKey, (old: any) => {
        if (!old) return old;
        return {
          ...old,
          isFollowing: shouldFollow,
          followersCount: shouldFollow
            ? (old.followersCount || 0) + 1
            : Math.max(0, (old.followersCount || 0) - 1),
        };
      });

      return { previousData, shouldFollow };
    },
    onSuccess: (data, variables, context) => {
      // Invalidate related queries using standardized query keys
      queryClient.invalidateQueries({
        queryKey: followingInfoQuery.queryKey,
      });

      // Also invalidate any user-related queries that might show follower counts
      queryClient.invalidateQueries({
        queryKey: ["user", followeeId],
      });

      // Also invalidate the current user's following list
      if (currentUser?.uid) {
        const followingListQuery = user.listFollowing({
          userId: currentUser.uid,
        });
        queryClient.invalidateQueries({
          queryKey: followingListQuery.queryKey,
        });
      }

      onSuccess?.(variables.shouldFollow);
    },
    onError: (error, variables, context) => {
      // Rollback optimistic update
      if (context?.previousData) {
        queryClient.setQueryData(
          followingInfoQuery.queryKey,
          context.previousData,
        );
      }

      onError?.(error instanceof Error ? error : new Error(String(error)));
    },
  });
}
